
import { useEffect, useRef, useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import ReadyToStartSection from '../../../components/feature/ReadyToStartSection';
import Button from '../../../components/base/Button';

export default function Tuition() {
  const [visibleRows, setVisibleRows] = useState<number[]>([]);
  const rowsRef = useRef<(HTMLDivElement | null)[]>([]);

  const tuitionRates = [
    {
      program: 'Infants (6 weeks - 12 months)',
      fullTime: 'Contact us for pricing',
      partTime: 'Contact us for pricing',
      dropIn: '$60/day',
      features: ['Low teacher-to-child ratio (1:3)', 'Individualized care plans', 'Flexible feeding schedules']
    },
    {
      program: 'Toddlers (12 months - 2 years)',
      fullTime: 'Contact us for pricing',
      partTime: 'Contact us for pricing',
      dropIn: '$55/day',
      features: ['Potty training support', 'Language development focus', 'Outdoor play time']
    },
    {
      program: 'Preschool (2 - 4 years)',
      fullTime: 'Contact us for pricing',
      partTime: 'Contact us for pricing',
      dropIn: '$50/day',
      features: ['Pre-academic skills', 'Creative arts program', 'Social skill development']
    },
    {
      program: 'Pre-K (4 - 5 years)',
      fullTime: 'Contact us for pricing',
      partTime: 'Contact us for pricing',
      dropIn: '$45/day',
      features: ['Kindergarten preparation', 'STEM activities', 'Reading readiness']
    }
  ];

  const additionalInfo = [
    {
      title: 'Registration Fee',
      description: 'One-time fee of $50 per child (non-refundable)',
      icon: 'ri-file-text-line'
    },
    {
      title: 'Sibling Discount',
      description: '10% discount on tuition for second child and beyond',
      icon: 'ri-group-line'
    },
    {
      title: 'Payment Options',
      description: 'Monthly, bi-weekly, or weekly payment plans available',
      icon: 'ri-calendar-check-line'
    },
    {
      title: 'Scholarship Program',
      description: 'Limited scholarships available for qualifying families',
      icon: 'ri-hand-heart-line'
    }
  ];

  useEffect(() => {
    const observers = rowsRef.current.map((row, index) => {
      if (!row) return null;
      
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisibleRows(prev => [...prev, index]);
            }, index * 60);
          }
        },
        { threshold: 0.2 }
      );
      
      observer.observe(row);
      return observer;
    });

    return () => {
      observers.forEach(observer => observer?.disconnect());
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FFF7E8]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#333333] mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Tuition & Fees
          </h1>
          <p className="text-xl text-[#333333]/80 max-w-3xl mx-auto" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Transparent pricing for quality early childhood education. We believe in providing exceptional value for your investment in your child's future.
          </p>
        </div>
      </section>

      {/* Tuition Table */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-[#A8D8F0] px-6 py-4">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                2024 Tuition Rates
              </h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#333333]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      Program
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-[#333333]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      Full-Time
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-[#333333]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      Part-Time
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-[#333333]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      Drop-In
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tuitionRates.map((rate, index) => (
                    <tr
                      key={index}
                      ref={el => rowsRef.current[index] = el}
                      className={`border-t border-gray-100 hover:bg-[#BFE6CC]/10 transition-all duration-200 ${
                        visibleRows.includes(index) 
                          ? 'translate-y-0 opacity-100' 
                          : 'translate-y-2 opacity-0'
                      }`}
                    >
                      <td className="px-6 py-6">
                        <div>
                          <div className="font-semibold text-[#333333] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                            {rate.program}
                          </div>
                          <ul className="text-sm text-[#333333]/70 space-y-1">
                            {rate.features.map((feature, featureIndex) => (
                              <li key={featureIndex} className="flex items-center">
                                <i className="ri-check-line text-[#BFE6CC] mr-2"></i>
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </td>
                      <td className="px-6 py-6 text-center">
                        <a 
                          href="/contact"
                          onClick={(e) => {
                            e.preventDefault();
                            if (window.REACT_APP_NAVIGATE) {
                              window.REACT_APP_NAVIGATE('/contact');
                            }
                          }}
                          className="cursor-pointer hover:underline"
                        >
                          <div className="text-lg font-bold text-[#A8D8F0]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                            {rate.fullTime}
                          </div>
                          <div className="text-sm text-[#333333]/60">410-676-9002</div>
                        </a>
                      </td>
                      <td className="px-6 py-6 text-center">
                        <a 
                          href="/contact"
                          onClick={(e) => {
                            e.preventDefault();
                            if (window.REACT_APP_NAVIGATE) {
                              window.REACT_APP_NAVIGATE('/contact');
                            }
                          }}
                          className="cursor-pointer hover:underline"
                        >
                          <div className="text-lg font-bold text-[#BFE6CC]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                            {rate.partTime}
                          </div>
                          <div className="text-sm text-[#333333]/60">410-676-9002</div>
                        </a>
                      </td>
                      <td className="px-6 py-6 text-center">
                        <div className="text-lg font-bold text-[#333333]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                          {rate.dropIn}
                        </div>
                        <div className="text-sm text-[#333333]/60">Per day</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Information */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Additional Information
            </h2>
            <p className="text-lg text-[#333333]/80 max-w-2xl mx-auto" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              Important details about fees, discounts, and payment options
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {additionalInfo.map((info, index) => (
              <div key={index} className="flex items-start p-6 bg-[#FFF7E8] rounded-xl">
                <div className="w-12 h-12 flex items-center justify-center bg-[#A8D8F0] rounded-full mr-4 flex-shrink-0">
                  <i className={`${info.icon} text-xl text-white`}></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#333333] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {info.title}
                  </h3>
                  <p className="text-[#333333]/80" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    {info.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Financial Assistance */}
      <section className="py-16 bg[#A8D8F0]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Button 
            variant="primary"
            onClick={() => window.location.href = 'mailto:info@CreativeJourneysChildCare.com'}
          >
            Contact Us About Financial Options
          </Button>
        </div>
      </section>

      {/* Scholarship Program Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#FFF7E8]">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-[#A8D8F0] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-graduation-cap-line text-4xl text-white"></i>
              </div>
              <p className="text-[#333333]/80 text-lg max-w-3xl mx-auto" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                We believe every child deserves access to quality early childhood education. Our scholarship program helps make Creative Journeys accessible to families who need financial support.
              </p>
            </div>
          </div>
        </div>
      </section>

      <ReadyToStartSection />
      <Footer />
    </div>
  );
}
